import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
  Alert,
  Badge,
} from "@mui/material";
import { generateAuditedClient } from "../audit/auditedClient";
import {
  Configs,
  GetUsersByTeamIdInput,
  Metadata,
  WriteAccessRequestsInput,
  AccessResolveInput,
  TicketInput,
} from "../models/API";
import {
  updateTeamUserMap,
  updateTeamUserAttributes,
  createUser,
  handleUserRequest,
  writeAccessRequests,
  processTicketyEvents,
} from "../graphql/mutations";
import { useTheme } from "@mui/material/styles";
import { getMetadata } from "../graphql/queries";
import ErrorModal from "./ErrorModal";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PeopleIcon from "@mui/icons-material/People";
import PendingActionsIcon from "@mui/icons-material/PendingActions";

interface AdminConsoleComponentProps {
  isOpen: boolean;
  menuWidth: number;
  currentTeamId: number;
  currentUserName: string;
  effectiveRole: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const AdminConsoleComponent: React.FC<AdminConsoleComponentProps> = ({
  isOpen,
  menuWidth,
  currentTeamId,
  currentUserName,
  effectiveRole,
}) => {
  const [error, setError] = useState<any>(null);
  const [, setMetadata] = useState<Metadata | null>(null);
  const client = generateAuditedClient();
  const theme = useTheme();
  const [metaRefreshFlag, setMetaRefresh] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [editRole, setEditRole] = useState<string>("user");
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [newUserName, setNewUserName] = useState("");
  const [teamUserMapData, setTeamUserMapData] = useState<any[]>([]);
  const [newGroupName, setNewGroupName] = useState("");
  const [newTeamName, setNewTeamName] = useState("");
  const [addMode, setAddMode] = useState<"user" | "group">("user");
  const [groupAlert, setGroupAlert] = useState<string>("");
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const [configs, setConfigs] = useState<Configs>({
    teamsCustomDict: {},
    teamsDict: {},
    teamsPendingUsersDict: {},
    teamsUsersDict: {},
    usersDict: {},
    userIDsDict: {},
    usersAttributesDict: {},
    usersTeamsDict: {},
  });

  useEffect(() => {
    if (isOpen) {
      fetchMetadata();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metaRefreshFlag, currentTeamId, isOpen]);

  if (!isOpen) {
    return null;
  }

  const fetchMetadata = async () => {
    try {
      const getUsersByTeamIdInput: GetUsersByTeamIdInput = {
        team_id: currentTeamId,
      };
      const metaData = await client.graphql({
        query: getMetadata,
        variables: {
          input: getUsersByTeamIdInput,
        },
      });
      // @ts-ignore
      const meta: Metadata = metaData.data;
      setMetadata(meta);

      // populate users
      const newConfigs = { ...configs };
      meta.getUsers.forEach((item) => {
        newConfigs.usersDict[item.id] = item.user_name;
        newConfigs.userIDsDict[item.user_name] = item.id;
        try {
          newConfigs.usersAttributesDict[item.id] = item.attributes
            ? JSON.parse(item.attributes)
            : {};
        } catch (e) {
          newConfigs.usersAttributesDict[item.id] = {};
        }
      });
      // populate teams
      meta.getTeams.forEach((item) => {
        newConfigs.teamsDict[item.id] = item.name;
        newConfigs.teamsCustomDict[item.id] = JSON.parse(
          item.custom_attributes
        );
      });
      // populate team_user_map
      newConfigs.teamsUsersDict[currentTeamId] = [];
      meta.getUsersByTeamId.forEach((item) => {
        newConfigs.teamsUsersDict[currentTeamId].push(item.id);
      });
      newConfigs.teamsPendingUsersDict[currentTeamId] = [];
      meta.getPendingUsersByTeamId.forEach((item) => {
        newConfigs.teamsPendingUsersDict[currentTeamId].push(item.id);
      });

      setTeamUserMapData(meta.getTeamUserMapByTeamId || []);
      setConfigs(newConfigs);
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      console.error(err);
      setError(err);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getUserRole = (userId: number): string => {
    const teamUserMap = teamUserMapData?.find(
      (tum: any) => tum.user_id === userId
    );
    if (teamUserMap && teamUserMap.attributes) {
      try {
        const attributes =
          typeof teamUserMap.attributes === "string"
            ? JSON.parse(teamUserMap.attributes)
            : teamUserMap.attributes;
        return attributes.role || "user";
      } catch (e) {
        console.error("Error parsing team user attributes:", e);
      }
    }
    return "user";
  };

  const handleEditRole = (userId: number) => {
    setEditingUserId(userId);
    setEditRole(getUserRole(userId));
  };

  const handleSaveRole = async () => {
    if (editingUserId === null) return;

    try {
      await client.graphql({
        query: updateTeamUserAttributes,
        variables: {
          input: {
            team_id: currentTeamId,
            user_id: editingUserId,
            attributes: JSON.stringify({ role: editRole }),
          },
        },
      });

      await new Promise((resolve) => setTimeout(resolve, 200));
      await fetchMetadata();
      setEditingUserId(null);
    } catch (err) {
      console.error("Error updating user role:", err);
      setError(err);
    }
  };

  const handleRemoveUser = async (userId: number) => {
    try {
      const updatedUserIds = configs.teamsUsersDict[currentTeamId].filter(
        (id) => id !== userId
      );
      await client.graphql({
        query: updateTeamUserMap,
        variables: {
          input: {
            team_id: currentTeamId,
            user_ids: updatedUserIds.join(),
          },
        },
      });
      setMetaRefresh(!metaRefreshFlag);
    } catch (err) {
      console.error("Error removing user:", err);
      setError(err);
    }
  };

  const handleAddUser = async () => {
    if (!newUserName.trim()) return;

    try {
      let userId: number;

      if (newUserName in configs.userIDsDict) {
        userId = configs.userIDsDict[newUserName];
      } else {
        const result = await client.graphql({
          query: createUser,
          variables: {
            input: { user_name: newUserName.trim() },
          },
        });
        userId = result.data.createUser.id;
      }

      const updatedUserIds = [...configs.teamsUsersDict[currentTeamId], userId];
      await client.graphql({
        query: updateTeamUserMap,
        variables: {
          input: {
            team_id: currentTeamId,
            user_ids: updatedUserIds.join(),
          },
        },
      });

      setNewUserName("");
      setAddUserDialogOpen(false);
      setMetaRefresh(!metaRefreshFlag);
    } catch (err) {
      console.error("Error adding user:", err);
      setError(err);
    }
  };

  // ---- Group Management Handlers ----

  const handleAddGroup = async () => {
    if (!newGroupName.trim() || !newTeamName.trim()) return;
    // Reuse the same flow as adding a user. The readable Permissions team name
    // becomes the user_name so the row is legible in the DB and the console; the
    // amzn1.abacus.team.* id goes in attributes.group_id, which is what
    // check_permissions_team resolves BRASS membership against.
    try {
      // Step 1: Create the pseudo-user keyed on the team name, stamping
      // group_id + team_name into attributes.
      const createResult = (await client.graphql({
        query: createUser,
        variables: {
          input: {
            user_name: newTeamName.trim(),
            group_id: newGroupName.trim(),
            team_name: newTeamName.trim(),
          },
        },
      })) as any;
      const newUserId = createResult.data?.createUser?.id;
      if (!newUserId) throw new Error("Failed to create group entry");

      // Step 2: Add to the team via team_user_map (same as adding a regular user)
      const userIds = teamUserMapData
        .map((m: any) => m.user_id)
        .concat(newUserId)
        .join(",");
      await client.graphql({
        query: updateTeamUserMap,
        variables: { input: { team_id: currentTeamId, user_ids: userIds } },
      });

      setNewGroupName("");
      setNewTeamName("");
      setAddUserDialogOpen(false);
      setAddMode("user");
      setGroupAlert("");
      setMetaRefresh(!metaRefreshFlag);
    } catch (err) {
      console.error("Error adding group:", err);
      const msg =
        (err as any)?.errors?.[0]?.message ||
        (err as any)?.message ||
        "Unable to add group. It may already exist or the ID is invalid.";
      setGroupAlert(msg);
    }
  };

  const handleRemoveGroup = (userId: number) => {
    // Groups are stored as users (user_name = readable team name, abacus id in
    // attributes.group_id). The row already knows its user_id, so remove it
    // directly via the existing team_user_map flow. Do NOT look the row up by
    // name — the button is handed the abacus group id, which does not match the
    // readable user_name stored in usersDict.
    if (userId) handleRemoveUser(userId);
  };

  const notifyUser = async (userId: number) => {
    try {
      const userName = configs.usersDict[userId];
      const notificationInput: WriteAccessRequestsInput = {
        user_id: userId,
        user_name: userName,
        team_id: currentTeamId,
        team_name: configs.teamsDict[currentTeamId],
      };
      await client.graphql({
        query: writeAccessRequests,
        variables: { input: notificationInput },
      });
    } catch (err) {
      console.error("Error notifying user:", err);
    }
  };

  const resolveSimTicket = async (userName: string, action: string) => {
    try {
      const accessResolve: AccessResolveInput = {
        appPrefix: "FINN",
        ticketOwner: "FN",
        severity: "SEV_3",
        userName: userName,
        teamName: configs.teamsDict[currentTeamId],
        action: action,
      };
      const simInput: TicketInput = {
        accessResolve: accessResolve,
      };
      await client.graphql({
        query: processTicketyEvents,
        variables: { input: simInput },
      });
    } catch (err) {
      console.error("Error resolving SIM ticket:", err);
    }
  };

  const handleApproveUser = async (userId: number) => {
    try {
      await client.graphql({
        query: handleUserRequest,
        variables: {
          input: {
            user_id: userId,
            team_id: currentTeamId,
            status: 0,
            updated_by: configs.userIDsDict[currentUserName],
          },
        },
      });
      const userName = configs.usersDict[userId];
      await notifyUser(userId);
      await resolveSimTicket(userName, "Approve");
      setMetaRefresh(!metaRefreshFlag);
    } catch (err) {
      console.error("Error approving user:", err);
      setError(err);
    }
  };

  const handleDenyUser = async (userId: number) => {
    try {
      await client.graphql({
        query: handleUserRequest,
        variables: {
          input: {
            user_id: userId,
            team_id: currentTeamId,
            status: 2,
            updated_by: configs.userIDsDict[currentUserName],
          },
        },
      });
      const userName = configs.usersDict[userId];
      await resolveSimTicket(userName, "Deny");
      setMetaRefresh(!metaRefreshFlag);
    } catch (err) {
      console.error("Error denying user:", err);
      setError(err);
    }
  };

  // A users row is a permission team ("group") rather than a person when it
  // carries attributes.group_id. Legacy rows predate that field and instead have
  // the raw Permissions group ID as their user_name, so fall back to the prefix.
  const isGroupRow = (userId: number) =>
    Boolean(
      configs.usersAttributesDict[userId]?.group_id ||
        configs.usersDict[userId]?.startsWith("amzn1.abacus.team.")
    );

  // The amzn1.abacus.team.* id for a group row, wherever it happens to live.
  const groupIdOf = (userId: number): string =>
    configs.usersAttributesDict[userId]?.group_id || configs.usersDict[userId];

  const teamGroupIds = (configs.teamsUsersDict[currentTeamId] || []).filter(
    isGroupRow
  );

  const filteredUsers =
    configs.teamsUsersDict[currentTeamId]
      ?.filter((userId) => userId <= 0 || userId >= 10)
      .filter((userId) => {
        const userName = configs.usersDict[userId];
        // Exclude group entries — they are rendered separately as links
        if (isGroupRow(userId)) return false;
        return userName?.toLowerCase().includes(searchQuery.toLowerCase());
      }) || [];

  const pendingUsers = configs.teamsPendingUsersDict[currentTeamId] || [];
  const totalUsers = filteredUsers.length;
  const adminCount = filteredUsers.filter(
    (id) => getUserRole(id) === "admin"
  ).length;
  const operatorCount = filteredUsers.filter(
    (id) => getUserRole(id) === "operator"
  ).length;

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        ml: `${menuWidth}px`,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: theme.transitions.create("margin-left", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.standard,
        }),
      }}
    >
      {/* Fixed Header */}
      <Box sx={{ flexShrink: 0, p: 2, pb: 0 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5" fontWeight={700}>
            Admin Console
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Team: {configs.teamsDict[currentTeamId]}
          </Typography>
        </Stack>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", px: 2, pb: 2 }}>
        {/* Statistics Cards */}
        <Stack direction="row" spacing={2} mb={3}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <PeopleIcon color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4">{totalUsers}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Users
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Badge badgeContent={pendingUsers.length} color="warning">
                  <PendingActionsIcon color="action" sx={{ fontSize: 40 }} />
                </Badge>
                <Box>
                  <Typography variant="h4">{pendingUsers.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Requests
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Role Distribution
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Chip
                    label={`${adminCount} Admin`}
                    color="error"
                    size="small"
                  />
                  <Chip
                    label={`${operatorCount} Operator`}
                    color="warning"
                    size="small"
                  />
                  <Chip
                    label={`${totalUsers - adminCount - operatorCount} User`}
                    size="small"
                  />
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Stack>

        {/* Tabs */}
        <Paper sx={{ mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="User Management" />
            <Tab
              label={
                <Badge badgeContent={pendingUsers.length} color="warning">
                  Pending Requests
                </Badge>
              }
            />
          </Tabs>
        </Paper>

        {/* User Management Tab */}
        <TabPanel value={tabValue} index={0}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <TextField
              placeholder="Search users..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setAddUserDialogOpen(true)}
            >
              Add User/Group
            </Button>
          </Stack>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User Name</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((userId) => {
                  const userName = configs.usersDict[userId];
                  const role = getUserRole(userId);
                  return (
                    <TableRow key={`${userId}-${refreshKey}`}>
                      <TableCell>{userName}</TableCell>
                      <TableCell>
                        <Chip
                          label={role}
                          size="small"
                          color={
                            role === "admin"
                              ? "error"
                              : role === "operator"
                              ? "warning"
                              : "default"
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit Role">
                          <IconButton
                            size="small"
                            onClick={() => handleEditRole(userId)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove User">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveUser(userId)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Group rows — users entries that carry a Permissions group ID */}
                {teamGroupIds
                  .filter(
                    (userId) =>
                      !searchQuery ||
                      configs.usersDict[userId]
                        ?.toLowerCase()
                        .includes(searchQuery.toLowerCase())
                  )
                  .map((userId) => {
                    const groupId = groupIdOf(userId);
                    const groupUserName = configs.usersDict[userId];
                    // Current rows carry the readable team name as user_name.
                    // Legacy rows have the raw id there, so prefer the name
                    // stashed in attributes and fall back to the id.
                    const groupDisplayName =
                      (groupUserName !== groupId ? groupUserName : "") ||
                      configs.usersAttributesDict[userId]?.team_name ||
                      groupId;
                    const groupRole = getUserRole(userId);
                    return (
                      <TableRow key={`group-${groupId}`}>
                        <TableCell>
                          <a
                            href={`https://access.a2z.com/group-management/team/${groupId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              textDecoration: "none",
                              color: "#1976d2",
                              fontSize: "inherit",
                            }}
                            title={groupId}
                          >
                            {groupDisplayName}
                          </a>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={groupRole}
                            size="small"
                            color={
                              groupRole === "operator" ? "warning" : "default"
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit Role">
                            <IconButton
                              size="small"
                              onClick={() => handleEditRole(userId)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove Group">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveGroup(userId)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {filteredUsers.length === 0 && teamGroupIds.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography variant="body2" color="text.secondary" py={2}>
                        No users or groups found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Pending Requests Tab */}
        <TabPanel value={tabValue} index={1}>
          {pendingUsers.length === 0 ? (
            <Alert severity="info">No pending access requests</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User Name</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingUsers.map((userId) => (
                    <TableRow key={userId}>
                      <TableCell>{configs.usersDict[userId]}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            onClick={() => handleApproveUser(userId)}
                            color="success"
                          >
                            <CheckIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Deny">
                          <IconButton
                            size="small"
                            onClick={() => handleDenyUser(userId)}
                            color="error"
                          >
                            <CloseIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Edit Role Dialog */}
        <Dialog
          open={editingUserId !== null}
          onClose={() => setEditingUserId(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit User Role</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={editRole}
                  onChange={(e: SelectChangeEvent) =>
                    setEditRole(e.target.value)
                  }
                  label="Role"
                >
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="operator">Operator</MenuItem>
                  {/* Groups can only be user or operator — admin must be assigned individually */}
                  {!(editingUserId && isGroupRow(editingUserId)) && (
                    <MenuItem value="admin">Admin</MenuItem>
                  )}
                </Select>
              </FormControl>
              <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Role Permissions:
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • <strong>User:</strong> Can create/modify/delete private
                  knowledge and data sources
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • <strong>Operator:</strong> Can create/modify/delete public
                  knowledge and data sources
                </Typography>
                <Typography variant="body2">
                  • <strong>Admin:</strong> Can access Admin Console and modify
                  user roles
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditingUserId(null)}>Cancel</Button>
            <Button onClick={handleSaveRole} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add User Dialog */}
        <Dialog
          open={addUserDialogOpen}
          onClose={() => {
            setAddUserDialogOpen(false);
            setAddMode("user");
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add User/Group to Team</DialogTitle>
          <DialogContent>
            <Tabs
              value={addMode === "user" ? 0 : 1}
              onChange={(_, v) => setAddMode(v === 0 ? "user" : "group")}
              sx={{ mb: 2 }}
            >
              <Tab icon={<PersonAddIcon />} label="User" iconPosition="start" />
              <Tab icon={<PeopleIcon />} label="Group" iconPosition="start" />
            </Tabs>

            {addMode === "user" ? (
              <TextField
                autoFocus
                margin="dense"
                label="User Name"
                fullWidth
                value={newUserName}
                onChange={(e) => setNewUserName(e.target.value)}
                helperText="Enter the user alias (no spaces allowed)"
              />
            ) : (
              <>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Team Name"
                  fullWidth
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  error={newGroupName.length > 0 && !newTeamName.trim()}
                  helperText="Enter a human-readable name for this permission team (e.g. SPB-Finance-AI). Shown in the members list."
                />
                <TextField
                  margin="dense"
                  label="Team ID"
                  fullWidth
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  error={
                    newGroupName.length > 0 &&
                    !newGroupName.startsWith("amzn1.abacus.team.")
                  }
                  helperText={
                    newGroupName.length > 0 &&
                    !newGroupName.startsWith("amzn1.abacus.team.")
                      ? "Team ID must start with amzn1.abacus.team. — find it on the team's permissions page."
                      : "Paste the team ID from Permissions Tool (e.g. amzn1.abacus.team.xxxx). All members will gain user access."
                  }
                />
              </>
            )}
            {groupAlert && addMode === "group" && (
              <Alert
                severity={
                  groupAlert.includes("no action needed") ? "info" : "warning"
                }
                sx={{ mt: 2 }}
                onClose={() => setGroupAlert("")}
              >
                {groupAlert}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setAddUserDialogOpen(false);
                setGroupAlert("");
                setAddMode("user");
                setNewTeamName("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={addMode === "user" ? handleAddUser : handleAddGroup}
              variant="contained"
              disabled={
                addMode === "user"
                  ? !newUserName.trim()
                  : !newGroupName.startsWith("amzn1.abacus.team.") ||
                    !newTeamName.trim()
              }
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>

        <ErrorModal error={error} onClose={() => setError(null)} />
      </Box>
    </Box>
  );
};

export default AdminConsoleComponent;
